/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { chartTooltip } from "@/components/common/ChartTooltip";
import { SpeedTestResult, TimeRange, PaginatedResponse } from "@/types/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getHistory, getPublicHistory } from "@/api/speedtest";
import { motion, AnimatePresence } from "motion/react";
import { formatters } from "@/utils/timeSettings";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronDownIcon } from "lucide-react";
import {
  FaDownload,
  FaUpload,
  FaClock,
  FaWaveSquare,
  FaGripVertical,
} from "react-icons/fa";
import { Button } from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface SpeedHistoryChartProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  isPublic?: boolean;
  hasAnyTests?: boolean;
  hasCurrentRangeTests?: boolean;
  showDragHandle?: boolean;
  dragHandleRef?: (node: HTMLElement | null) => void;
  dragHandleListeners?: Record<string, (...args: unknown[]) => unknown>;
  dragHandleClassName?: string;
  // Server filtering props
  serverFilterMode?: "all" | "single" | "multiple";
  selectedSingleServer?: string;
  selectedMultipleServers?: Set<string>;
  onServerFilterModeChange?: (mode: "all" | "single" | "multiple") => void;
  onSelectedSingleServerChange?: (server: string) => void;
  onSelectedMultipleServersChange?: (servers: Set<string>) => void;
  // Multiple server display mode
  multipleServerDisplayMode?: "overlay" | "separate";
  onMultipleServerDisplayModeChange?: (mode: "overlay" | "separate") => void;
}

interface VisibleMetrics {
  download: boolean;
  upload: boolean;
  latency: boolean;
  jitter: boolean;
}

const timeRangeOptions: { value: TimeRange; label: string }[] = [
  { value: "1d", label: "24 Hours" },
  { value: "3d", label: "3 Days" },
  { value: "1w", label: "1 Week" },
  { value: "1m", label: "1 Month" },
  { value: "all", label: "All Time" },
];

const ChartSkeleton: React.FC = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="animate-ping h-full w-full"
  >
    <div className="h-full w-full bg-gray-200/50 dark:bg-gray-800/50 rounded-lg" />
  </motion.div>
);

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

export const SpeedHistoryChart: React.FC<SpeedHistoryChartProps> = ({
  timeRange = "1w",
  onTimeRangeChange,
  isPublic = false,
  hasAnyTests = false,
  hasCurrentRangeTests = false,
  showDragHandle = false,
  dragHandleRef,
  dragHandleListeners,
  dragHandleClassName,
  // Server filtering props
  serverFilterMode: propServerFilterMode,
  selectedSingleServer: propSelectedSingleServer,
  selectedMultipleServers: propSelectedMultipleServers,
  onServerFilterModeChange,
  onSelectedSingleServerChange,
  onSelectedMultipleServersChange,
  // Multiple server display mode props
  multipleServerDisplayMode: propMultipleServerDisplayMode,
  onMultipleServerDisplayModeChange,
}) => {
  const isMobile = useIsMobile();

  const [visibleMetrics, setVisibleMetrics] = useState<VisibleMetrics>(() => {
    const saved = localStorage.getItem("speedtest-visible-metrics");
    return saved
      ? JSON.parse(saved)
      : {
          download: true,
          upload: true,
          latency: true,
          jitter: true,
        };
  });

  // Server filtering state - use props if provided, otherwise internal state
  const [internalServerFilterMode, setInternalServerFilterMode] = useState<"all" | "single" | "multiple">("all");
  const [internalSelectedSingleServer, setInternalSelectedSingleServer] = useState<string>("all");
  const [internalSelectedMultipleServers, setInternalSelectedMultipleServers] = useState<Set<string>>(new Set());
  const [internalMultipleServerDisplayMode, setInternalMultipleServerDisplayMode] = useState<"overlay" | "separate">("overlay");

  // Use props if provided, otherwise use internal state
  const serverFilterMode = propServerFilterMode ?? internalServerFilterMode;
  const selectedSingleServer = propSelectedSingleServer ?? internalSelectedSingleServer;
  const selectedMultipleServers = propSelectedMultipleServers ?? internalSelectedMultipleServers;
  const multipleServerDisplayMode = propMultipleServerDisplayMode ?? internalMultipleServerDisplayMode;

  const handleMetricToggle = (key: keyof VisibleMetrics) => {
    setVisibleMetrics((prev: VisibleMetrics) => {
      const newState = {
        ...prev,
        [key]: !prev[key],
      };
      localStorage.setItem(
        "speedtest-visible-metrics",
        JSON.stringify(newState)
      );
      return newState;
    });
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["history-chart", timeRange, 500, isPublic],
      queryFn: async ({ pageParam = 1 }) => {
        const historyFn = isPublic ? getPublicHistory : getHistory;
        const response = await historyFn(timeRange, pageParam, 500);
        return response;
      },
      getNextPageParam: (
        lastPage: PaginatedResponse<SpeedTestResult>,
        allPages
      ) => {
        // If the last page has no data, there are no more pages.
        if (!lastPage.data || lastPage.data.length === 0) {
          return undefined;
        }

        const totalFetched = allPages.reduce(
          (acc, page) => acc + (page.data?.length || 0),
          0
        );

        // If the total fetched is greater than or equal to the total available, no more pages.
        if (totalFetched >= (lastPage.total ?? 0)) {
          return undefined;
        }

        // Otherwise, return the next page number.
        return lastPage.page + 1;
      },
      initialPageParam: 1,
    });

  const filteredData = useMemo(() => {
    if (!data) return [];

    const allData = data.pages.flatMap(
      (page: PaginatedResponse<SpeedTestResult>) => page.data || []
    );

    return allData
      .filter((item): item is SpeedTestResult => item != null)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .map((item) => ({
        rawTimestamp: item.createdAt,
        timestamp: formatters.chartTick(item.createdAt, timeRange),
        download: Number(item.downloadSpeed) || 0,
        upload: Number(item.uploadSpeed) || 0,
        latency: Number(parseFloat(typeof item.latency === 'string' ? item.latency.replace("ms", "") : item.latency) || 0),
        jitter: Number(item.jitter) || 0,
        serverName: item.serverName || "Unknown Server",
        serverHost: item.serverHost || item.serverName || "Unknown Server",
        testType: item.testType || "speedtest",
      }))
      .filter(
        (item) =>
          !isNaN(item.download) &&
          !isNaN(item.upload) &&
          !isNaN(item.latency) &&
          !isNaN(item.jitter)
      );
  }, [data]);

  // Extract available servers from results
  const availableServers = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    
    const serverMap = new Map();
    filteredData.forEach(result => {
      if (result.serverName && !serverMap.has(result.serverName)) {
        serverMap.set(result.serverName, {
          id: result.serverName,
          name: result.serverName,
          host: result.serverHost || result.serverName
        });
      }
    });
    
    return Array.from(serverMap.values());
  }, [filteredData]);

  // Apply server filtering
  const allResults = useMemo(() => {
    if (serverFilterMode === "single" && selectedSingleServer !== "all") {
      const filtered = filteredData.filter(result => result.serverName === selectedSingleServer);
      return filtered;
    } else if (serverFilterMode === "multiple" && selectedMultipleServers.size > 0) {
      const filtered = filteredData.filter(result => 
        selectedMultipleServers.has(result.serverName) || selectedMultipleServers.has(result.serverHost)
      );
      return filtered;
    }
    
    return filteredData;
  }, [filteredData, serverFilterMode, selectedSingleServer, selectedMultipleServers, availableServers]);

  // Server filter handlers
  const handleServerDropdownChange = (value: string) => {
    
    if (value === "all") {
      if (onServerFilterModeChange) onServerFilterModeChange("all");
      else setInternalServerFilterMode("all");
      
      if (onSelectedSingleServerChange) onSelectedSingleServerChange("all");
      else setInternalSelectedSingleServer("all");
    } else if (value === "multiple") {
      if (onServerFilterModeChange) onServerFilterModeChange("multiple");
      else setInternalServerFilterMode("multiple");
    } else {
      if (onServerFilterModeChange) onServerFilterModeChange("single");
      else setInternalServerFilterMode("single");
      
      if (onSelectedSingleServerChange) onSelectedSingleServerChange(value);
      else setInternalSelectedSingleServer(value);
    }
  };

  const handleServerCheckboxChange = (serverId: string, checked: boolean) => {
    const updateFunction = (prev: Set<string>) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(serverId);
      } else {
        newSet.delete(serverId);
      }
      return newSet;
    };

    if (onSelectedMultipleServersChange) {
      const newSet = updateFunction(selectedMultipleServers);
      onSelectedMultipleServersChange(newSet);
    } else {
      setInternalSelectedMultipleServers(updateFunction);
    }
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    localStorage.setItem("speedtest-time-range", range);
    onTimeRangeChange(range);
  };

  // Function to render individual server charts for separate mode
  const renderServerChart = (serverName: string, serverData: any[]) => {
    return (
      <div key={`server-${serverName}`} className="mb-6">
        <div className="mb-2">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {serverName} ({serverData.length} data points)
          </h4>
        </div>
        <Card className="h-80 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={serverData}
              margin={
                isMobile
                  ? { top: 5, right: 5, left: 0, bottom: 5 }
                  : { top: 5, right: 30, left: 20, bottom: 25 }
              }
            >
              <defs>
                <linearGradient id={`downloadGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#86b9a0" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#86b9a0" stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`uploadGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9aa39c" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#9aa39c" stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`latencyGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#d9b26a" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#d9b26a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`jitterGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6e8f83" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6e8f83" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                horizontal={true}
                vertical={false}
                stroke="var(--chart-text)"
                opacity={0.3}
              />
              
              <XAxis
                dataKey="rawTimestamp"
                height={isMobile ? 50 : 60}
                tickMargin={isMobile ? 5 : 10}
                tick={{ fontSize: isMobile ? 11 : 12, fill: "var(--chart-text)" }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  });
                }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                yAxisId="speed"
                label={{
                  value: "Speed (Mbps)",
                  position: "insideLeft",
                  angle: -90,
                  offset: 0,
                  style: {
                    textAnchor: "middle",
                    fill: "var(--chart-text)",
                  },
                  dy: 0,
                }}
                tick={{ fontSize: 12, fill: "var(--chart-text)" }}
                width={45}
                domain={[0, "auto"]}
                allowDataOverflow={false}
              />

              <YAxis
                yAxisId="latency"
                orientation="right"
                label={{
                  value: "ms",
                  position: "insideRight",
                  angle: -90,
                  offset: 0,
                  style: { fill: "var(--chart-text)" },
                }}
                tick={{ fontSize: 12, fill: "var(--chart-text)" }}
                width={45}
                domain={[0, "auto"]}
                allowDataOverflow={false}
              />

              <Tooltip
                {...chartTooltip({
                  formatter: (value, name) => {
                    if (typeof value === "number") {
                      if (name === "Download" || name === "Upload") return [`${value.toFixed(1)} Mbps`, name];
                      if (name === "Latency" || name === "Jitter") return [`${value.toFixed(1)} ms`, name];
                    }
                    return [value, name];
                  },
                  labelFormatter: (timestamp) => new Date(Number(timestamp)).toLocaleString(),
                })}
              />

              {visibleMetrics.download && (
                <Area
                  key={`download-${serverName}`}
                  yAxisId="speed"
                  type="monotone"
                  dataKey="download"
                  name="Download"
                  stroke="#86b9a0"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6 }}
                  fill={`url(#downloadGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')})`}
                  className="!stroke-blue-400"
                  animationDuration={1750}
                  animationBegin={0}
                  isAnimationActive={true}
                />
              )}
              
              {visibleMetrics.upload && (
                <Area
                  key={`upload-${serverName}`}
                  yAxisId="speed"
                  type="monotone"
                  dataKey="upload"
                  name="Upload"
                  stroke="#9aa39c"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6 }}
                  fill={`url(#uploadGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')})`}
                  className="!stroke-emerald-400"
                  animationDuration={1750}
                  animationBegin={150}
                  isAnimationActive={true}
                />
              )}

              {visibleMetrics.latency && (
                <Area
                  key={`latency-${serverName}`}
                  yAxisId="latency"
                  type="monotone"
                  dataKey="latency"
                  name="Latency"
                  stroke="#d9b26a"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  fill={`url(#latencyGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')})`}
                  className="!stroke-amber-400"
                  animationDuration={1750}
                  animationBegin={300}
                  isAnimationActive={true}
                />
              )}

              {visibleMetrics.jitter && (
                <Area
                  key={`jitter-${serverName}`}
                  yAxisId="latency"
                  type="monotone"
                  dataKey="jitter"
                  name="Jitter"
                  stroke="#6e8f83"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  fill={`url(#jitterGradient-${serverName.replace(/[^a-zA-Z0-9]/g, '_')})`}
                  className="!stroke-purple-400"
                  strokeDasharray="5 5"
                  animationDuration={1750}
                  animationBegin={0}
                  isAnimationActive={true}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>
    );
  };

  // Function to render charts based on display mode
  const renderChartsBasedOnMode = () => {
    if (serverFilterMode === "multiple" && selectedMultipleServers.size > 1 && multipleServerDisplayMode === "separate") {
      // Group processed data by server for separate charts
      const serverGroups: { [key: string]: any[] } = {};
      
      // allResults is already filtered for selected multiple servers
      allResults.forEach(result => {
        const serverKey = result.serverName || result.serverHost || "Unknown";
        if (!serverGroups[serverKey]) {
          serverGroups[serverKey] = [];
        }
        serverGroups[serverKey].push(result);
      });

      return (
        <div className="space-y-6">
          {Object.entries(serverGroups).map(([serverName, serverData]) => (
            renderServerChart(serverName, serverData)
          ))}
        </div>
      );
    }
    
    // Default: show overlay chart (existing behavior)
    return chart;
  };

  const chart = useMemo(
    () => (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          key={`${timeRange}-${allResults.length}`}
          data={allResults}
          margin={
            isMobile
              ? { top: 5, right: 5, left: 0, bottom: 5 }
              : { top: 5, right: 30, left: 20, bottom: 25 }
          }
        >
          <defs>
            <linearGradient id="downloadGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#86b9a0" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#86b9a0" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="uploadGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#9aa39c" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#9aa39c" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="latencyGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#d9b26a" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#d9b26a" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="jitterGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6e8f83" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#6e8f83" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={true}
            vertical={false}
            stroke="var(--chart-text)"
            opacity={0.3}
          />
          <XAxis
            dataKey="rawTimestamp"
            height={isMobile ? 50 : 60}
            tickMargin={isMobile ? 5 : 10}
            tick={{ fontSize: isMobile ? 11 : 12, fill: "var(--chart-text)" }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return formatters.chartTick(date, timeRange, isMobile);
            }}
          />
          <YAxis
            yAxisId="speed"
            label={
              isMobile
                ? {
                    value: "Mbps",
                    position: "insideLeft",
                    angle: -90,
                    offset: 0,
                    style: {
                      textAnchor: "middle",
                      fill: "var(--chart-text)",
                      fontSize: 11,
                    },
                    dy: 40,
                  }
                : {
                    value: "Speed (Mbps)",
                    position: "insideLeft",
                    angle: -90,
                    offset: 0,
                    style: {
                      textAnchor: "middle",
                      fill: "var(--chart-text)",
                    },
                    dy: 0,
                  }
            }
            tick={{ fontSize: isMobile ? 11 : 12, fill: "var(--chart-text)" }}
            tickFormatter={(value) => (isMobile ? Math.round(value) : value)}
            width={isMobile ? 35 : 45}
            domain={[0, "auto"]}
            allowDataOverflow={false}
          />
          <YAxis
            yAxisId="latency"
            orientation="right"
            label={
              isMobile
                ? {
                    value: "ms",
                    position: "insideRight",
                    angle: -90,
                    offset: 0,
                    style: {
                      textAnchor: "middle",
                      fill: "var(--chart-text)",
                      fontSize: 11,
                    },
                    dy: 20,
                  }
                : {
                    value: "ms",
                    position: "insideRight",
                    angle: -90,
                    offset: 0,
                    style: { fill: "var(--chart-text)" },
                  }
            }
            tick={{ fontSize: isMobile ? 11 : 12, fill: "var(--chart-text)" }}
            tickFormatter={(value) => (isMobile ? Math.round(value) : value)}
            width={isMobile ? 30 : 45}
            domain={[0, "auto"]}
            allowDataOverflow={false}
          />
          <Tooltip
            {...chartTooltip({
              formatter: (value, name) => {
                const v = Number(value);
                if (name === "Download" || name === "Upload") return [`${v.toFixed(isMobile ? 1 : 2)} Mbps`, name];
                return [`${v.toFixed(isMobile ? 1 : 2)} ms`, name];
              },
              labelFormatter: (label, payload) => {
                const data = payload?.[0]?.payload as
                  | { testType?: string; timestamp?: string; serverHost?: string; serverName?: string }
                  | undefined;
                if (!data) return String(label);
                const shouldRedact = isPublic && (data.testType === "iperf3" || data.testType === "librespeed");
                const formattedDate = data.timestamp || formatters.chartTooltip(label as never, timeRange);
                return (
                  <>
                    {formattedDate}
                    <span className="gh-tip-sub">
                      {shouldRedact ? "redacted host" : data.serverHost || data.serverName}
                      {data.testType && (
                        <span className="gh-tip-muted">{`(${data.testType === "speedtest" ? "speedtest.net" : data.testType})`}</span>
                      )}
                    </span>
                  </>
                );
              },
            })}
            // work on touch screens too
            trigger={isMobile ? "click" : "hover"}
          />
          {visibleMetrics.download && (
            <Area
              key="download"
              yAxisId="speed"
              type="monotone"
              dataKey="download"
              name="Download"
              stroke="#86b9a0"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
              fill="url(#downloadGradient)"
              className="!stroke-blue-400"
              animationDuration={1750}
              animationBegin={0}
              isAnimationActive={true}
              strokeDasharray="0"
            />
          )}
          {visibleMetrics.upload && (
            <Area
              key="upload"
              yAxisId="speed"
              type="monotone"
              dataKey="upload"
              name="Upload"
              stroke="#9aa39c"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
              fill="url(#uploadGradient)"
              className="!stroke-emerald-400"
              animationDuration={1750}
              animationBegin={0}
              isAnimationActive={true}
              strokeDasharray="0"
            />
          )}
          {visibleMetrics.latency && (
            <Area
              key="latency"
              yAxisId="latency"
              type="monotone"
              dataKey="latency"
              name="Latency"
              stroke="#d9b26a"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              fill="url(#latencyGradient)"
              className="!stroke-amber-400"
              strokeDasharray="3 3"
              animationDuration={1750}
              animationBegin={0}
              isAnimationActive={true}
            />
          )}
          {visibleMetrics.jitter && (
            <Area
              key="jitter"
              yAxisId="latency"
              type="monotone"
              dataKey="jitter"
              name="Jitter"
              stroke="#6e8f83"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              fill="url(#jitterGradient)"
              className="!stroke-purple-400"
              strokeDasharray="5 5"
              animationDuration={1750}
              animationBegin={0}
              isAnimationActive={true}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    ),
    [allResults, timeRange, visibleMetrics, isMobile, isPublic, availableServers]
  );

  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem("speedtest-chart-open");
    return saved === null ? true : saved === "true";
  });

  // Persist chart open state to localStorage
  useEffect(() => {
    localStorage.setItem("speedtest-chart-open", isOpen.toString());
  }, [isOpen]);

  // Persist time range to localStorage
  useEffect(() => {
    localStorage.setItem("speedtest-time-range", timeRange);
  }, [timeRange]);

  return (
    <div className="glass-panel overflow-hidden mb-6">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex flex-col h-full">
          <CollapsibleTrigger asChild>
            <button
              className={cn(
                "flex justify-between items-center w-full px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0",
                "text-left transition-colors touch-manipulation"
              )}
            >
              <div className="flex items-center gap-2">
                {showDragHandle && (
                  <div
                    ref={dragHandleRef}
                    {...dragHandleListeners}
                    className={cn(
                      "cursor-grab active:cursor-grabbing touch-none p-1 -m-1",
                      dragHandleClassName
                    )}
                  >
                    <FaGripVertical className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                  </div>
                )}
                <h2 className="text-gray-900 dark:text-white text-lg sm:text-xl font-semibold p-1 select-none">
                  Speedtest History
                </h2>
              </div>
              <div className="p-1 -m-1">
                <ChevronDownIcon
                  className={cn(
                    "w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform duration-200",
                    isOpen && "transform rotate-180"
                  )}
                />
              </div>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
              className="px-3 sm:px-4 flex-1"
            >
              <div className="pt-2 sm:pt-0 pb-3 sm:pb-4">
                {/* Controls */}
                <div className="flex flex-col gap-3 mb-4">
                  {/* Metric Toggle Controls */}
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-2 w-full sm:w-auto">
                    {[
                      {
                        key: "download",
                        color: "#86b9a0",
                        label: "Download",
                        icon: <FaDownload size={14} />,
                      },
                      {
                        key: "upload",
                        color: "#9aa39c",
                        label: "Upload",
                        icon: <FaUpload size={14} />,
                      },
                      {
                        key: "latency",
                        color: "#d9b26a",
                        label: "Latency",
                        icon: <FaClock size={14} />,
                      },
                      {
                        key: "jitter",
                        color: "#6e8f83",
                        label: "Jitter",
                        icon: <FaWaveSquare size={14} />,
                      },
                    ].map(({ key, label, icon, color }) => {
                      const isActive =
                        visibleMetrics[key as keyof typeof visibleMetrics];
                      return (
                        <button
                          key={key}
                          onClick={() =>
                            handleMetricToggle(
                              key as keyof typeof visibleMetrics
                            )
                          }
                          aria-label={`${
                            isActive ? "Hide" : "Show"
                          } ${label} metric`}
                          aria-pressed={isActive}
                          className={cn(
                            "relative px-2.5 sm:px-2 py-2 sm:py-1.5",
                            "text-[11px] sm:text-xs font-medium",
                            "flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1.5",
                            "rounded-md sm:rounded-md transition-all duration-200",
                            "border border-transparent",
                            // Reduced touch target size for mobile
                            "min-h-[36px] sm:min-h-0",
                            "flex-1 sm:flex-initial",
                            isActive
                              ? "bg-opacity-20 shadow-sm"
                              : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700",
                            // Add active states for touch feedback
                            "active:scale-95 sm:active:scale-100"
                          )}
                          style={{
                            backgroundColor: isActive
                              ? `${color}15`
                              : undefined,
                            borderColor: isActive ? `${color}40` : undefined,
                            color: isActive ? color : undefined,
                          }}
                        >
                          <div
                            className={cn(
                              "w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-200",
                              isActive ? "scale-110" : "scale-90 opacity-60"
                            )}
                            style={{
                              backgroundColor: isActive
                                ? color
                                : "currentColor",
                            }}
                          />
                          <span className="hidden sm:inline">{label}</span>
                          <div className="sm:hidden flex items-center gap-1">
                            {icon}
                            <span>{label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Desktop layout - Time Range Controls alongside metrics */}
                  <div className="hidden sm:flex sm:justify-end sm:-mt-12 sm:gap-3">
                    {/* Server Filter Controls */}
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">Server:</span>
                      
                      {/* Server Selection Dropdown */}
                      <Select
                        value={serverFilterMode === "multiple" ? "multiple" : selectedSingleServer}
                        onValueChange={handleServerDropdownChange}
                      >
                        <SelectTrigger className="w-[180px] px-3 py-1.5 text-xs">
                          <SelectValue placeholder="Select servers..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All servers</SelectItem>
                          <SelectItem value="multiple">Select multiple...</SelectItem>
                          {availableServers.map((server) => (
                            <SelectItem key={server.id} value={server.id}>
                              {server.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Multiple Server Selection Popover */}
                      {serverFilterMode === "multiple" && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="text-xs">
                              Multiple ({selectedMultipleServers.size})
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3">
                            <div className="space-y-2">
                              <div className="font-medium text-sm">Select Servers:</div>
                              {availableServers.length > 0 ? (
                                <div className="cc-scroll space-y-2 max-h-48 overflow-y-auto">
                                  {availableServers.map((server) => (
                                    <div key={server.id} className="flex items-center space-x-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                                      <Checkbox
                                        id={server.id}
                                        checked={selectedMultipleServers.has(server.id)}
                                        onCheckedChange={(checked) => {
                                          handleServerCheckboxChange(server.id, checked as boolean);
                                        }}
                                      />
                                      <label htmlFor={server.id} className="text-sm cursor-pointer flex-1">
                                        {server.name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">
                                  No servers available
                                </div>
                              )}
                              
                              {/* Display Mode Selection */}
                              {selectedMultipleServers.size > 0 && (
                                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="font-medium text-sm mb-2">Display Mode:</div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id="overlay-mode"
                                      checked={multipleServerDisplayMode === "overlay"}
                                      onCheckedChange={(checked) => {
                                        const mode = checked ? "overlay" : "separate";
                                        if (onMultipleServerDisplayModeChange) onMultipleServerDisplayModeChange(mode);
                                        else setInternalMultipleServerDisplayMode(mode);
                                      }}
                                    />
                                    <label htmlFor="overlay-mode" className="text-sm cursor-pointer">
                                      Overlay on same chart
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <Checkbox
                                      id="separate-mode"
                                      checked={multipleServerDisplayMode === "separate"}
                                      onCheckedChange={(checked) => {
                                        const mode = checked ? "separate" : "overlay";
                                        if (onMultipleServerDisplayModeChange) onMultipleServerDisplayModeChange(mode);
                                        else setInternalMultipleServerDisplayMode(mode);
                                      }}
                                    />
                                    <label htmlFor="separate-mode" className="text-sm cursor-pointer">
                                      Show in separate charts
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>

                    {/* Time Range */}
                    <Select
                      value={timeRange}
                      onValueChange={handleTimeRangeChange}
                    >
                      <SelectTrigger className="w-[140px] px-4 py-2 bg-gray-200/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-900 rounded-lg text-gray-700 dark:text-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg">
                        {timeRangeOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Mobile layout - Full width time selector */}
                  <div className="sm:hidden w-full">
                    <Select
                      value={timeRange}
                      onValueChange={handleTimeRangeChange}
                    >
                      <SelectTrigger className="w-full px-4 py-2 bg-gray-200/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-900 rounded-lg text-gray-700 dark:text-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                        {timeRangeOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Chart Area */}
                <div className={
                  serverFilterMode === "multiple" && selectedMultipleServers.size > 1 && multipleServerDisplayMode === "separate"
                    ? "min-h-[400px] -mx-3 sm:mx-0" // Dynamic height for multiple separate charts
                    : "h-[250px] sm:h-[300px] md:h-[400px] -mx-3 sm:mx-0" // Fixed height for single/overlay charts
                }>
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <ChartSkeleton />
                    ) : hasAnyTests &&
                      !hasCurrentRangeTests &&
                      filteredData.length === 0 ? (
                      <motion.div
                        key="no-data-message"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                        className="h-full flex items-center justify-center"
                      >
                        <div className="text-center">
                          <h3 className="text-gray-900 dark:text-white text-lg font-medium mb-2">
                            No tests in the last{" "}
                            {timeRange === "1d"
                              ? "24 hours"
                              : timeRange === "3d"
                              ? "3 days"
                              : timeRange === "1w"
                              ? "week"
                              : timeRange === "1m"
                              ? "month"
                              : "selected period"}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400">
                            Try selecting a different time range to view your
                            test history.
                          </p>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="chart"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                        className="h-full"
                      >
                        {renderChartsBasedOnMode()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {hasNextPage && !isLoading && filteredData.length > 0 && (
                  <div className="flex justify-end">
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Button
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        isLoading={isFetchingNextPage}
                        className="mb-4"
                      >
                        {isFetchingNextPage ? "Loading more..." : "Load more"}
                      </Button>
                    </motion.div>
                  </div>
                )}
              </div>
            </motion.div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};
